import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Table } from "react-bootstrap";
import { useGetOperationChangeHistoryQuery } from "@me-team/host/main/operationChangeHistory/graphql/operationChangeHistory.hooks";
import { useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import ErrorService from "../../../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { Spinner } from "@services/ui-components";
import { formatDate } from "../../../HistoryChangeAppointment/utils/utils";
import { findPreviousWithField, getFullName, operationActionToTranslationKey } from "../utils";
import { OperationHistoryDataItem } from "../../../index";
import { OperationHistoriesType } from "../types";
import { StorageProductRelationHistoryAction } from "@me-team/host/main/graphql/types";

const OperationHistoryTable: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId, operationId, companyId } = useParams();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);

    const { data: operationHistoryData, loading: operationHistoryLoading } = useGetOperationChangeHistoryQuery({
        variables: {
            companyId: Number(companyId),
            branchId: Number(branchId),
            operationId: Number(operationId),
        },
        skip: !operationId || !branchId || !companyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const operationHistory =
        operationHistoryData?.user?.company?.branches?.[0].storageProductRelationHistoryPaginated?.entities?.[0];
    const productName: string = operationHistory?.product.name;
    const operationName: StorageProductRelationHistoryAction = operationHistory?.historyAction;
    const reversedHistories: OperationHistoriesType[] = operationHistory?.operationHistories
        ? [...operationHistory.operationHistories].reverse()
        : [];
    const originalHistories: OperationHistoriesType[] = operationHistory?.operationHistories || [];

    if (operationHistoryLoading) return <Spinner />;

    return (
        <>
            <Container fluid className="table-responsive scrollbar position-relative p-0 mt-4 pt-6">
                <Table className="border-top">
                    <tbody>
                        {reversedHistories.length > 0 &&
                            reversedHistories.map((historyItem, reversedIndex) => {
                                const actionType: string = historyItem.exists ? "Editing" : "Creation";
                                const originalIndex = originalHistories.length - 1 - reversedIndex;
                                const historyDate = historyItem.storageProductOperationHistoryData?.find(
                                    (d) => d.field === "date"
                                )?.value;

                                const mandatoryFields =
                                    actionType === "Creation"
                                        ? [
                                              {
                                                  field: "Operation",
                                                  value: t(operationActionToTranslationKey[operationName]),
                                              },
                                              {
                                                  field: "Booking date",
                                                  value:
                                                      historyDate || formatDate(historyItem?.changeTime, i18n.language),
                                              },
                                              { field: "Product name", value: productName },
                                          ]
                                        : [];

                                const otherFields = (historyItem.storageProductOperationHistoryData || []).filter(
                                    (data) => !["date", "action"].includes(data.field)
                                );

                                const hasRealChanges =
                                    actionType === "Creation" ||
                                    otherFields.some((field) => {
                                        const prevField = findPreviousWithField(
                                            originalHistories,
                                            originalIndex,
                                            field.field
                                        );
                                        return !prevField || prevField.value !== field.value;
                                    });

                                if (!hasRealChanges) {
                                    return null;
                                }

                                return (
                                    <tr key={historyItem?.id}>
                                        <td className="ps-0 py-4">
                                            <div>
                                                {getFullName(historyItem.employee)}
                                                {" – "}
                                                <span className="text-grey">{t(actionType)}</span>
                                                {" – "}
                                                {formatDate(historyItem?.changeTime, i18n.language)}
                                            </div>
                                        </td>
                                        <td className="py-4 fs-7">
                                            {mandatoryFields.map((field, idx) => (
                                                <OperationHistoryDataItem
                                                    key={`mandatory_${idx}`}
                                                    historyData={{ field: field.field, value: field.value }}
                                                    isCreate={actionType === "Creation"}
                                                    isMandatory={true}
                                                />
                                            ))}
                                            {otherFields
                                                .filter((field) => {
                                                    if (actionType === "Creation") return true;
                                                    const prevField = findPreviousWithField(
                                                        originalHistories,
                                                        originalIndex,
                                                        field.field
                                                    );
                                                    return prevField ? prevField.value !== field.value : true;
                                                })
                                                .map((historyData, idx) => {
                                                    const prevData = findPreviousWithField(
                                                        originalHistories,
                                                        originalIndex,
                                                        historyData.field
                                                    );
                                                    return (
                                                        <OperationHistoryDataItem
                                                            key={`other_${idx}`}
                                                            historyData={historyData}
                                                            isCreate={actionType === "Creation"}
                                                            previousData={prevData}
                                                            operationType={operationName}
                                                        />
                                                    );
                                                })}
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </Table>
            </Container>

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default OperationHistoryTable;
